import { useEffect, useMemo, useState } from 'react';

import { apiClientFetchSynonymSearchApiKey } from '#/api/apiFetchSynonymSearchApiKey';
import type { ApiJobSynonymId } from '#/api/types/ApiTypedId';
import { AlgoliaSearchSelect } from '#/components/AlgoliaSearchSelect/AlgoliaSearchSelect';
import { Field } from '#/components/Form/Field/Field';
import { FieldDescription } from '#/components/Form/Field/FieldDescription';
import { FieldLabel } from '#/components/Form/Field/FieldLabel';
import type { FormUsageContext } from '#/components/Form/Form';
import { Form } from '#/components/Form/Form';
import { Icon } from '#/components/Icon/Icon';
import { useAppConfig } from '#/config/AppConfigContext';
import { useAlgoliaConfigByIndex } from '#/hooks/useAlgoliaConfigByIndex';
import { useI18n } from '#/i18n/hooks/useI18n';
import { salaryApiUpdateCareerPathSynonyms } from '#/modules/salary/api/salaryApiUpdateCareerPathSynonyms';
import type { CareerPathAnalyticsData } from '#/modules/salary/types/CareerPathAnalyticsData';
import type { EstimatedRange } from '#/modules/salary/types/EstimatedRange';
import { getSearchAlgoliaConfigWithApiKey } from '#/modules/search/algolia/searchAlgoliaConfig';
import { trackClicked } from '#/utils/analytics/track/trackClicked';
import { trackEvent } from '#/utils/analytics/track/trackEvent';

import {
  AddTitlesIconContainer,
  CareerPathFormContainer,
  CareerPathFormText,
  Container,
  JobTitleFieldContainer,
  StyledPrimaryButton,
} from './CareerPathForm.styled';
import { filterCareerPathAddTitleHits } from './filterCareerPathAddTitleHits';

type CareerPathFormProps = {
  analyticsTitle: string;
  usageContext: FormUsageContext;
  analyticsData: CareerPathAnalyticsData;
  careerPathData: EstimatedRange[];
  salaryModelCreated: number;
  setCareerPathData: (careerPath: EstimatedRange[]) => void;
  city?: string;
  selectedSynonymId: ApiJobSynonymId;
  stateCode?: string;
  searchApiKey: string | null;
  setSearchApiKey: (arg0: string) => void;
  yearsOfExperience?: number;
};

export function CareerPathForm({
  analyticsTitle,
  usageContext,
  analyticsData,
  careerPathData,
  setCareerPathData,
  salaryModelCreated,
  selectedSynonymId,
  city,
  stateCode,
  yearsOfExperience,
  searchApiKey,
  setSearchApiKey,
}: CareerPathFormProps) {
  const { appConfig } = useAppConfig();
  const { i18n } = useI18n();
  const { algoliaConfigByIndex } = useAlgoliaConfigByIndex();

  const [initialized, setInitialized] = useState(false);
  const [transitioned, setTransitioned] = useState(false);

  const filterKey = useMemo(() => {
    const careerPathKey = careerPathData
      .map((careerPath) =>
        [careerPath.synonymId, careerPath.jobTitleId, careerPath.jobTitleName]
          .filter(Boolean)
          .join(':'),
      )
      .join('|');

    return [selectedSynonymId, salaryModelCreated, careerPathKey]
      .filter(Boolean)
      .join('::');
  }, [careerPathData, salaryModelCreated, selectedSynonymId]);

  useEffect(() => {
    setInitialized(false);
  }, [city, setInitialized, selectedSynonymId, stateCode, yearsOfExperience]);

  useEffect(() => {
    setTransitioned(false);
  }, [city, setInitialized, selectedSynonymId, stateCode, yearsOfExperience]);

  const initializeCareerPathForm = () => {
    setInitialized(true);
    trackClicked('Career Path Add Title', analyticsData);
  };

  const initialValues = {};
  const onTransitionEnd = ({ propertyName }: { propertyName: string }) => {
    if (propertyName === 'clip-path') {
      setTransitioned(true);
    }
  };
  const onSubmit = async ({
    handleError,
    updateValues,
    updateSubmitting,
    values,
  }: // oxlint-disable-next-line typescript/no-explicit-any
  any) => {
    if (!values.additionalSynonymId) {
      updateSubmitting(false);
      return;
    }

    const updatedSynonymIds = [
      ...careerPathData
        .filter((cp) => cp.synonymId !== selectedSynonymId)
        .map((cp) => cp.synonymId),
      values.additionalSynonymId,
    ];

    const careerPath = await salaryApiUpdateCareerPathSynonyms({
      selectedSynonymId,
      city,
      stateCode,
      yearsOfExperience,
      currentSynonymIds: updatedSynonymIds,
      synonymIdToAdd: values.additionalSynonymId,
    });
    // Mimic form error when a null career path value exists
    if (careerPath.find((career) => career.salary === null)) {
      const error = {
        responseJson: {
          errors: [
            {
              type: 'not_enough_data',
              name: 'synonymId',
              description: ' ', // @HACK: Blank space keeps error as truthy w/o displaying text
            },
          ],
        },
      };
      handleError(error);
      updateSubmitting(false);
    } else {
      setCareerPathData(careerPath);
      updateValues({
        additionalSynonymName: '',
        additionalSynonymId: '',
        additionalSynonymStandardName: '',
        synonymId: '',
      });
      updateSubmitting(false);
      trackEvent('Career Path Success Added Title', {
        ...analyticsData,
        job_titles_displayed: careerPath.map((estimatedRange) => ({
          synonym_name: estimatedRange.synonymName,
          standard_name: estimatedRange.jobTitleName,
        })),
        job_title_added: {
          synonym_name: values.additionalSynonymName,
          standard_name: values.additionalSynonymStandardName,
        },
      });
    }
  };

  return (
    <Form
      analyticsTitle={analyticsTitle}
      usageContext={usageContext}
      qaId="career-path-chart-form"
      initialValues={initialValues}
      onSubmit={onSubmit}
      render={({ errors, clearErrors, submitForm }) => (
        <Container>
          <CareerPathFormContainer
            $hasError={Boolean(errors.synonymId)}
            $initialized={initialized}
            $transitioned={transitioned}
            onTransitionEnd={onTransitionEnd}
          >
            <CareerPathFormText>
              <FieldLabel>
                {errors.synonymId
                  ? i18n('Oops! Try another title')
                  : i18n('Add your own titles')}
              </FieldLabel>
              <FieldDescription>
                {errors.synonymId
                  ? i18n(
                      "We don't currently have enough data to add this title to your career path.",
                    )
                  : i18n('Start typing to see suggestions')}
              </FieldDescription>
            </CareerPathFormText>
            <JobTitleFieldContainer>
              <Field
                name="synonymId"
                inputProps={{ searchApiKey, filterKey }}
                renderInput={({ qaId }) => (
                  <AlgoliaSearchSelect<{
                    objectID: string;
                    name: string;
                    jobTitle: { id?: string; name: string };
                  }>
                    instanceId="career-path-synonym"
                    qaId={qaId}
                    styleOptions={{
                      // This menu z-index has to be high enough to be over
                      // the chart below it
                      menuZIndex: 200,
                    }}
                    onChange={(newOption) => {
                      if (!newOption) return;

                      clearErrors(['selectedSynonymId']);
                      void submitForm({
                        additionalSynonymId: newOption.value.objectID,
                        additionalSynonymName: newOption.value.name,
                        additionalSynonymStandardName:
                          newOption.value.jobTitle.name,
                      });
                    }}
                    searchArgs={{
                      config: searchApiKey
                        ? getSearchAlgoliaConfigWithApiKey({
                            apiKey: searchApiKey,
                            appConfig,
                          })
                        : undefined,
                      ...algoliaConfigByIndex.SALARY_REPORT_SURVEY_TITLES,
                      options: {
                        ...algoliaConfigByIndex.SALARY_REPORT_SURVEY_TITLES
                          .options,
                        filters: `includeInSurvey:true AND jobTitle.created < ${salaryModelCreated}`,
                      },
                      onApiKeyTimeout: () =>
                        apiClientFetchSynonymSearchApiKey().then(
                          (newApiKey) => {
                            setSearchApiKey(newApiKey);
                            return newApiKey;
                          },
                        ),
                    }}
                    getOptionLabel={(option) => option.value.name}
                    responseToOptions={(hits) => {
                      return filterCareerPathAddTitleHits({
                        hits,
                        careerPathData,
                        selectedSynonymId,
                      }).map((hit) => ({ label: hit.name, value: hit }));
                    }}
                  />
                )}
              ></Field>
            </JobTitleFieldContainer>
          </CareerPathFormContainer>
          <StyledPrimaryButton
            type="button"
            variant="primary"
            qaId="career-path-chart-form-button"
            $initialized={initialized}
            $transitioned={transitioned}
            onClick={() => initializeCareerPathForm()}
          >
            <AddTitlesIconContainer>
              {i18n('Add Titles')}
              <Icon name="plus" size={16} />
            </AddTitlesIconContainer>
          </StyledPrimaryButton>
        </Container>
      )}
    />
  );
}
